<template>
    <div class="qa-drawer">
        <div class="hox-drawer__container qa-drawer__container">
            <div class="hox-drawer__content qa-drawer__content">
                <div class="hox-drawer__content-container qa-drawer__content-container">
                    <div v-if="awaitingReviewCount !== null" class="qa-drawer__bulk-header">
                        {{ awaitingReviewCount }} to review
                    </div>
                    <div class="hox-drawer__header qa-drawer__header">
                        <QaDrawerHeader
                            :deliverables="creativeDeliverables"
                            :buttons-disabled="headerButtonsDisabled"
                            :is-bulk="isBulk"
                            :bulk-current="pageNumber"
                            :bulk-total="totalItems"
                            :visible-master-template-ids="visibleMasterTemplateIds"
                            @bulkIndexChange="setPageNumber"
                            @showResolved="onShowResolvedChange"
                            @statusHasBeenUpdated="onDeliverableStatusUpdate"
                            @userQaStatusHasBeenUpdated="onUserQaStatusUpdate"
                        />
                    </div>

                    <TabCarousel
                        v-model="pageNumber"
                        class="hox-drawer__body qa-drawer__body"
                        :total-pages="totalItems"
                        :show-arrows="hasDeliverables && isBulk"
                    >
                        <hox-loading-layer
                            v-if="isCreativeLoading"
                            class="qa-drawer__loading-layer"
                            :is-full-screen="false"
                            :left-offset-pixels="loaderOffset"
                            fix
                        />
                        <QaCreativePreview
                            v-if="!(isFilterBulk && !internalDeliverable)"
                            :deliverable="internalDeliverable"
                            :selected-annotation="selectedAnnotation"
                            :external-annotations="selectedDeliverableAnnotationsByType[AnnotationType.External]"
                            :has-error-loading-selected-deliverable-data="hasErrorLoadingSelectedDeliverableData"
                            :internal-annotations="selectedDeliverableAnnotationsByType[AnnotationType.Internal]"
                            :is-loading-selected-deliverable-data="isLoadingSelectedDeliverableData"
                            :show-full-creative="false"
                            :show-master-template-ids="currentCreativeMasterTemplateIds"
                            :playback-namespace="playbackNamespace"
                            @annotationSelected="setSelectedAnnotation"
                            @annotationAdded="addAnnotation"
                            @select="setSelectedDeliverable"
                            @statusHasBeenUpdated="onDeliverableStatusUpdate"
                            @userQaStatusHasBeenUpdated="onUserQaStatusUpdate"
                            @loadingStarted="isCreativeLoading = true"
                            @loadingFinished="isCreativeLoading = false"
                            @visibleMasterTemplateIds="setVisibleMasterTemplateIds"
                            @load="onLoad"
                        />
                    </TabCarousel>

                    <PreviewGroupControls
                        v-if="hasDeliverables"
                        :scrubbar="{
                            currentTime: playbackController.currentTime.value,
                            duration: playbackController.duration.value
                        }"
                        :play-pause-button="{ isPlaying: playbackController.isPlaying.value }"
                        @playPause="playbackController.playPause"
                        @setBannerPlaybackTime="playbackController.seek"
                    >
                        <template #annotations>
                            <AnnotationScrubbar
                                v-if="showAnnotationsOnTimeline"
                                :key="selectedDeliverable?.idHash"
                                :show-resolved="areResolvedShown"
                                :annotations="selectedDeliverableAnnotations"
                                :duration="playbackController.duration.value || maxDuration"
                                :selected-annotation="selectedAnnotation"
                                @annotationSelected="setSelectedAnnotation"
                            />
                        </template>
                    </PreviewGroupControls>
                </div>

                <div v-if="hasDeliverables" class="qa-drawer__sidebar">
                    <QaDrawerSidebar
                        :show-resolved="areResolvedShown"
                        :deliverable="selectedDeliverable"
                        :external-annotations="selectedDeliverableAnnotationsByType[AnnotationType.External]"
                        :has-error-loading-selected-deliverable-data="hasErrorLoadingSelectedDeliverableData"
                        :internal-annotations="selectedDeliverableAnnotationsByType[AnnotationType.Internal]"
                        :is-loading-selected-deliverable-data="isLoadingSelectedDeliverableData"
                        :selected-annotation="selectedAnnotation"
                        @annotationDeleted="removeAnnotation"
                        @annotationSelected="setSelectedAnnotation"
                        @annotationUpdated="replaceAnnotation"
                        @loadSelectedDeliverableData="getSelectedDeliverableData"
                        @resolvedShown="onShowResolvedChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
// TODO: UPDATE HERE

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import UsersByScopeQuery from "@/apollo/queries/UsersByScope.gql";
import BulkAwaitingReviewCountQuery from "@/apollo/queries/v2/BulkAwaitingReviewCount.gql";
import { getIdHash, getDeliverableIdentityFromObject } from "@/utils/generateHash";

import QaDrawerHeader from "@/components/Qa/DrawerTab/QaDrawerTabHeader.vue";
import QaDrawerSidebar from "@/components/Qa/DrawerTab/QaDrawerTabSidebar.vue";
import AnnotationScrubbar from "@/components/Qa/AnnotationScrubbar.vue";
import QaCreativePreview from "@/components/Qa/QaCreativePreview.vue";
import TabCarousel from "@/components/TabCarousel.vue";
import PreviewGroupControls from "@/components/PlaybackControls/PreviewGroupControls.vue";

import { AnnotationType } from "@/enums/annotations";
import { BannerDefaultDuration, BannerQaUserStatus, BannerQaUserStatusLabelFilterMap } from "@/enums/banners";

import getPaginatedDeliverables from "@/services/deliverables/getPaginatedDeliverables";
import { getAnnotationsOfDeliverable, getDeliverables } from "@/services/Qa";
import URLManager from "@/services/URLManager";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import { CampaignAction } from "@/store/modules/campaign";
import { BannerNamespace, usePlaybackController } from "@/components/PlaybackControls/usePlaybackController";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { mapStores } from "pinia";

import type { ConstructedDeliverable, QaAnnotation } from "@/types/types";
import type { LanguageCode } from "@/utils/languages";

const QueryParams = {
    SelectedDeliverableId: "qad-sd",
    ShowResolvedAnnotations: "qad-sra"
};

export default defineComponent({
    name: "QaDrawerTab",

    components: {
        TabCarousel,
        QaCreativePreview,
        AnnotationScrubbar,
        QaDrawerHeader,
        QaDrawerSidebar,
        PreviewGroupControls
    },

    props: {
        allSelectedMode: {
            type: Boolean,
            default: false
        },

        deliverables: {
            type: Array as PropType<ConstructedDeliverable[]>,
            required: true
        }
    },

    emits: [
        "annotationsHasBeenUpdated",
        "selectedDeliverableUpdate",
        "statusHasBeenUpdated",
        "userStatusHasBeenUpdated"
    ],

    setup() {
        const playbackNamespace = BannerNamespace.Qa;
        const playbackController = usePlaybackController(playbackNamespace);

        return {
            playbackController,
            playbackNamespace
        };
    },

    data() {
        return {
            areResolvedShown: false,
            awaitingReviewCount: null,
            creativeDeliverables: [] as ConstructedDeliverable[],
            existingDeliverableStatusIdHashes: new Set(),
            fetchedDeliverable: null as ConstructedDeliverable | null,
            fetchedTotal: 0,
            isCreativeLoading: false,
            hasErrorLoadingSelectedDeliverableAnnotations: false,
            hasErrorLoadingSelectedDeliverableStatus: false,
            isLoadingSelectedDeliverableAnnotations: false,
            isLoadingSelectedDeliverableStatus: false,
            pageNumber: 1,
            selectedAnnotation: null as QaAnnotation | null,
            selectedDeliverable: null as ConstructedDeliverable | null,
            selectedDeliverableAnnotations: [] as QaAnnotation[],
            visibleMasterTemplateIds: [] as string[],
            urlManager: null as URLManager | null,
            AnnotationType
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        clientId() {
            return this.$route.params.clientId as string;
        },

        maxDuration() {
            return this.$store.state.campaign.defaultTimelineLength || BannerDefaultDuration;
        },

        creatives() {
            if (this.isArrayBulk) {
                const result = this.deliverables.reduce(
                    (acc, deliverable) => {
                        let creative = {};
                        const deliverableIdentity = getDeliverableIdentityFromObject(deliverable);

                        const creativeIdentity = {
                            ...deliverableIdentity,
                            masterTemplateId: null
                        };

                        const creativeId = getIdHash(creativeIdentity);

                        if (creativeId in acc) {
                            creative = {
                                creativeId,
                                editableGroupIds: creativeIdentity.editableGroupValueIds,
                                language: creativeIdentity.language,
                                deliverableIds: [...acc[creativeId].deliverableIds, deliverable._id],
                                deliverables: {
                                    ...acc[creativeId].deliverables,
                                    [deliverable._id]: deliverable
                                }
                            };
                        } else {
                            creative = {
                                creativeId,
                                editableGroupIds: creativeIdentity.editableGroupValueIds,
                                language: creativeIdentity.language,
                                deliverableIds: [deliverable._id],
                                deliverables: {
                                    [deliverable._id]: deliverable
                                }
                            };
                        }

                        return Object.assign(acc, { [creativeId]: creative });
                    },
                    {} as Record<
                        string,
                        {
                            creativeId: string;
                            deliverableIds: string[];
                            deliverables: Record<string, ConstructedDeliverable>;
                            editableGroupIds: string[];
                            language: LanguageCode;
                        }
                    >
                );

                return result;
            }

            return null;
        },

        creativeIds() {
            if (!this.isArrayBulk || !this.creatives) {
                return [];
            }

            return Object.keys(this.creatives);
        },

        currentCreative() {
            return this.creatives?.[this.creativeIds[this.pageNumber - 1]];
        },

        currentCreativeMasterTemplateIds() {
            if (this.isArrayBulk) {
                const { deliverables, deliverableIds } = this.currentCreative!;

                return deliverableIds.map(deliverableId => {
                    return deliverables[deliverableId].masterTemplate._id;
                });
            }

            if (this.isFilterBulk) {
                return this.creativeLibraryFiltersStore.selectedSizes || [];
            }

            return [];
        },

        hasDeliverables() {
            return this.creativeDeliverables.length !== 0;
        },
        hasErrorLoadingSelectedDeliverableData() {
            return this.hasErrorLoadingSelectedDeliverableAnnotations || this.hasErrorLoadingSelectedDeliverableStatus;
        },

        headerButtonsDisabled() {
            return this.creativeDeliverables.length === 0;
        },

        internalDeliverable() {
            if (this.isArrayBulk && this.currentCreative) {
                const { deliverables, deliverableIds } = this.currentCreative;
                return deliverables[deliverableIds[0]];
            }

            if (this.isFilterBulk) {
                return this.fetchedDeliverable!;
            }

            return this.deliverables[0];
        },

        isArrayBulk() {
            return Array.isArray(this.deliverables) && this.deliverables.length > 1;
        },

        isFilterBulk() {
            return this.allSelectedMode;
        },

        isBulk() {
            return this.isArrayBulk || this.isFilterBulk;
        },

        isLoadingSelectedDeliverableData() {
            return this.isLoadingSelectedDeliverableAnnotations || this.isLoadingSelectedDeliverableStatus;
        },

        loaderOffset() {
            return this.hasDeliverables ? -300 : 0;
        },

        selectedDeliverableAnnotationsByType() {
            if (this.hasErrorLoadingSelectedDeliverableData) {
                return {
                    [AnnotationType.Internal]: [],
                    [AnnotationType.External]: []
                };
            }

            const annotationsByType = this.selectedDeliverableAnnotations.reduce(
                (acc, annotation) => {
                    acc[annotation.type].push({
                        ...annotation,
                        displayIndex: acc[annotation.type].length + 1
                    });
                    return acc;
                },
                {
                    [AnnotationType.Internal]: [],
                    [AnnotationType.External]: []
                } as Record<(typeof AnnotationType)[keyof typeof AnnotationType], QaAnnotation[]>
            );

            /*
                While these should be prefiltered by the backend we're going to be
                extra careful and ensure that they user does not see annotations that
                they do not have access to on the frontend too.
            */
            if (!this.userCanListExternalAnnotations) {
                annotationsByType[AnnotationType.External] = [];
            }

            if (!this.userCanListInternalAnnotations) {
                annotationsByType[AnnotationType.Internal] = [];
            }

            /*
                We reverse the arrays to show the most recent annotation first.

                We could have done this by using Array.unshift to put items onto
                the array, but then we would not have been able to index the annotations
                with the most recent annotation having the highest value.
            */
            return {
                [AnnotationType.Internal]: annotationsByType[AnnotationType.Internal].reverse(),
                [AnnotationType.External]: annotationsByType[AnnotationType.External].reverse()
            };
        },
        showAnnotationsOnTimeline() {
            return this.selectedDeliverableAnnotations.length > 0 && !this.hasErrorLoadingSelectedDeliverableData;
        },

        totalItems() {
            if (this.isFilterBulk) {
                return this.fetchedTotal;
            }

            if (this.isArrayBulk) {
                return this.creativeIds.length;
            }

            return 1;
        },

        userCanListExternalAnnotations() {
            return this.$auth.userCan(this.$auth.Actions.CanListExternalAnnotations);
        },
        userCanListInternalAnnotations() {
            return this.$auth.userCan(this.$auth.Actions.CanListInternalAnnotations);
        },
        userCanOnlyListExternalAnnotations() {
            return !this.userCanListInternalAnnotations && this.userCanListExternalAnnotations;
        },
        userCanOnlyListInternalAnnotations() {
            return this.userCanListInternalAnnotations && !this.userCanListExternalAnnotations;
        },
        userHasAccessToAnnotationType() {
            if (this.userCanOnlyListInternalAnnotations) {
                return AnnotationType.Internal;
            }
            if (this.userCanOnlyListExternalAnnotations) {
                return AnnotationType.External;
            }
            return undefined;
        }
    },
    watch: {
        areResolvedShown() {
            this.setShowResolvedAnnotationsQueryParams();
        },

        selectedDeliverable() {
            this.selectedDeliverableAnnotations = [];
            this.selectedAnnotation = null;
            this.getSelectedDeliverableData();
            if (!this.isBulk) {
                this.setSelectedDeliverableIdQueryParams();
            }
        },

        pageNumber: {
            handler(page: number, oldPage: number) {
                if (this.isFilterBulk) {
                    this.fetchDeliverable(page);
                }

                if (page !== oldPage) {
                    // clear selection on page change
                    this.setSelectedDeliverable(null);
                }
            }
        },

        visibleMasterTemplateIds() {
            if (this.isFilterBulk) {
                this.getAwaitingReviewCount();
            }
        }
    },

    beforeUnmount() {
        this.cleanDeliverableStatusByIdHash();
        this.unsetAllQueryParams();
    },

    created() {
        this.urlManager = new URLManager(this.$router, {
            tracked: Object.values(QueryParams)
        });
        /*
            We store a set of existing deliverable ID hashes so that we can
            clear out data created by this component without removing data that
            is being used in elsewhere.
        */

        this.existingDeliverableStatusIdHashes = new Set(
            Object.keys(this.$store.state.deliverableLibrary.deliverableStatusByIdHash)
        );

        this.getSelectedDeliverableData();

        this.areResolvedShown = !!this.$route.query[QueryParams.ShowResolvedAnnotations];
        this.setShowResolvedAnnotationsQueryParams();

        if (this.isFilterBulk) {
            this.fetchDeliverable(this.pageNumber);
            this.getAwaitingReviewCount();
        }
    },

    methods: {
        addAnnotation(annotation: QaAnnotation) {
            this.selectedDeliverableAnnotations = [...this.selectedDeliverableAnnotations, annotation];
            this.getSelectedDeliverableData();
            this.$emit("annotationsHasBeenUpdated");
        },

        cleanDeliverableStatusByIdHash() {
            /*
                To ensure that `deliverableStatusByIdHash` in the store does not
                infinitely grow we need to clear out data that is no longer being used.

                To make sure that we do not clear out data that is being used elsewhere
                we filter out any deliverables that existed in `deliverableStatusByIdHash`
                before this component was created.
            */
            const deliverablesToUnset = this.creativeDeliverables.filter(
                deliverable => !this.existingDeliverableStatusIdHashes.has(deliverable.idHash)
            );

            this.$store.dispatch(DeliverableLibraryAction.UnsetDeliverableStatusByIdHash, deliverablesToUnset);
        },

        async fetchDeliverable(page: number) {
            const filters = this.creativeLibraryFiltersStore.getConstructedLibraryFilters;
            const libraryFilters = {
                ...filters,
                filters: {
                    ...filters.filters,
                    // Let's get just a single size as this is enough to determine the creative
                    masterTemplateIds: [filters.filters.masterTemplateIds[0]]
                }
            };

            const { deliverables, pagination } = await getPaginatedDeliverables({
                campaignId: this.campaignId,
                libraryFilters,
                page: page,
                deliverablesPerPage: 1,
                store: this.$store
            });

            this.fetchedDeliverable = deliverables.pop()!;
            this.fetchedTotal = pagination.totalCount;
        },

        async getAnnotationsForSelectedDeliverable() {
            this.hasErrorLoadingSelectedDeliverableAnnotations = false;
            this.isLoadingSelectedDeliverableAnnotations = true;
            if (
                !this.selectedDeliverable ||
                (!this.userCanListInternalAnnotations && !this.userCanListExternalAnnotations)
            ) {
                /*
                    The user has no permissions to see any annotations or there is no
                    selected annotation: don't bother with the query.
                */
                this.selectedDeliverableAnnotations = [];
                return;
            }
            try {
                this.selectedDeliverableAnnotations = await getAnnotationsOfDeliverable(
                    this.campaignId,
                    this.selectedDeliverable,
                    this.userHasAccessToAnnotationType
                );
            } catch {
                this.selectedDeliverableAnnotations = [];
                this.hasErrorLoadingSelectedDeliverableAnnotations = true;
            }
            this.isLoadingSelectedDeliverableAnnotations = false;
        },

        async getAwaitingReviewCount() {
            if (!this.visibleMasterTemplateIds.length || !this.isFilterBulk) {
                return;
            }

            const filters = this.creativeLibraryFiltersStore.getConstructedLibraryFilters;

            const libraryFiltersVars = {
                ...filters,
                filters: {
                    ...filters.filters,
                    masterTemplateIds: this.visibleMasterTemplateIds
                },
                userStatus: BannerQaUserStatusLabelFilterMap[BannerQaUserStatus.None]
            };

            const { data } = await this.$apollo.query({
                fetchPolicy: "no-cache",
                query: BulkAwaitingReviewCountQuery,
                variables: {
                    campaignId: this.campaignId,
                    libraryFilters: libraryFiltersVars
                }
            });
            this.awaitingReviewCount = data.computeDeliverables.pagination.totalCount;
        },

        getSelectedDeliverableData() {
            this.getAnnotationsForSelectedDeliverable();
            this.getStatusForSelectedDeliverable();
        },

        async getStatusForSelectedDeliverable() {
            this.hasErrorLoadingSelectedDeliverableStatus = false;
            this.isLoadingSelectedDeliverableStatus = true;
            if (this.selectedDeliverable) {
                try {
                    const deliverablesStatus = await getDeliverables(this.campaignId, [this.selectedDeliverable]);
                    this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverablesStatus);
                } catch {
                    this.hasErrorLoadingSelectedDeliverableStatus = true;
                }
            }
            this.isLoadingSelectedDeliverableStatus = false;
        },

        onUserQaStatusUpdate() {
            this.getAwaitingReviewCount();
            this.$emit("userStatusHasBeenUpdated");
        },

        onDeliverableStatusUpdate() {
            this.$emit("statusHasBeenUpdated");
        },

        onLoad(deliverables: ConstructedDeliverable[]) {
            this.creativeDeliverables = deliverables;
        },

        onShowResolvedChange(val: boolean) {
            this.areResolvedShown = val;
        },

        removeAnnotation(annotationToRemove: QaAnnotation) {
            this.selectedDeliverableAnnotations = this.selectedDeliverableAnnotations.filter(
                annotation => annotation._id !== annotationToRemove._id
            );
            this.getSelectedDeliverableData();
            this.$emit("annotationsHasBeenUpdated");
        },

        replaceAnnotation(annotationToReplace: QaAnnotation) {
            this.selectedDeliverableAnnotations = this.selectedDeliverableAnnotations.map(annotation => {
                if (annotation._id === annotationToReplace._id) {
                    return annotationToReplace;
                }
                return annotation;
            });
            this.$emit("annotationsHasBeenUpdated");
            this.getSelectedDeliverableData();
        },

        setPageNumber(number: number) {
            this.pageNumber = number;
        },

        setShowResolvedAnnotationsQueryParams() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: QueryParams.ShowResolvedAnnotations,
                        value: this.areResolvedShown ? ["1"] : []
                    }
                ],
                true
            );
        },
        setSelectedAnnotation(annotation: QaAnnotation) {
            this.selectedAnnotation = annotation;
        },
        setSelectedDeliverable(deliverable: ConstructedDeliverable | null) {
            this.selectedDeliverable = deliverable;
            this.$emit("selectedDeliverableUpdate", deliverable);
        },
        setSelectedDeliverableIdQueryParams() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: QueryParams.SelectedDeliverableId,
                        value: this.selectedDeliverable ? [this.selectedDeliverable.idHash] : []
                    }
                ],
                true
            );
        },

        setVisibleMasterTemplateIds(visibleMasterTemplateIds: string[]) {
            this.visibleMasterTemplateIds = visibleMasterTemplateIds;
        },

        unsetAllQueryParams() {
            this.urlManager?.setURLParams(
                Object.values(QueryParams).map(key => ({
                    key,
                    value: []
                })),
                true
            );
        }
    },
    apollo: {
        usersByScope: {
            query: UsersByScopeQuery,
            variables() {
                return {
                    scope: {
                        clientId: this.clientId,
                        campaignId: this.campaignId
                    }
                };
            },

            result({ data, error }) {
                if (!error) {
                    this.$store.dispatch(CampaignAction.SetCampaignUsers, data.usersByScope);
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

$qa-sidebar-width: 300px;

.qa-drawer {
    .timeline-slider {
        align-items: center;
        display: flex;
        flex: 1 0;
        width: 100%;
    }

    .ivu-slider-wrap {
        margin: 9px ($spacing + 9) 9px 9px;
    }

    .tab-carousel-arrow.right {
        right: $spacing-large + $qa-sidebar-width;
    }
}

.qa-drawer__bulk-header {
    width: 100%;
    padding: $spacing-small $spacing-large $spacing-smaller $spacing-large;
    text-align: right;
    font-weight: bold;
    background-color: $white;
}

.qa-drawer__loading-layer {
    position: fixed;
}

.qa-drawer__scrubbar-container {
    flex: 1;
    position: relative;
}

.qa-drawer__sidebar {
    border-left: 1px solid $grey3;
    flex: 0 1;
    min-width: $qa-sidebar-width;
    position: relative;
    width: $qa-sidebar-width;
}
</style>
